import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { BasicDialog } from 'components';
import { InspectionRouteActions } from 'constants/inspections';
import {
  tour_03,
  tour_06,
  tour_18,
  TOUR_18_MUTED_LEVELS_END,
  TOUR_18_MUTED_LEVELS_START,
} from 'constants/tour';
import { useGlobalStore } from 'stores';
import {
  BoxPanel,
  StyledTab,
  StyledTabs,
  TabPanelContainer,
} from './TabCreateInspection.style';

// Guida al primo accesso, tour_18 (attività L3), step "Livelli 0,1,2" — div
// invisibile posizionato in assoluto, VERO bersaglio dello step (reactour
// evidenzia un solo elemento per selettore), dimensionato sul bounding box
// unito delle tab L0/L2 marcate in TabCreateInspection/costants.js, stessa
// tecnica già usata in InspectionsArchive.jsx per le colonne Livelli/
// Tipologia attività. Le due tab marcate esistono sempre in `tabs` (sono
// `muted`, non condizionali), quindi questo si monta senza danno anche
// quando tour_18 non è in uso (attività L0,1,2) — nessuno referenzia la sua
// classe in quel caso.
const MutedLevelsTourHighlight = ({ containerRef }) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const updateRect = () => {
      const container = containerRef.current;
      const start = document.querySelector(`.${TOUR_18_MUTED_LEVELS_START}`);
      const end = document.querySelector(`.${TOUR_18_MUTED_LEVELS_END}`);
      if (!container || !start || !end) {
        setRect(null);
        return;
      }
      const containerRect = container.getBoundingClientRect();
      const startRect = start.getBoundingClientRect();
      const endRect = end.getBoundingClientRect();
      setRect({
        top: startRect.top - containerRect.top,
        left: startRect.left - containerRect.left,
        width: endRect.right - startRect.left,
        height: startRect.height,
      });
    };

    updateRect();

    if (!containerRef.current || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(updateRect);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [containerRef]);

  if (!rect) {
    return null;
  }

  return (
    <div
      className={tour_18.step_02}
      style={{ position: 'absolute', pointerEvents: 'none', ...rect }}
    />
  );
};

MutedLevelsTourHighlight.propTypes = {
  containerRef: PropTypes.object.isRequired,
};

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <TabPanelContainer
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <BoxPanel
          sx={{ width: '100%', backgroundColor: '#FFFFFF', padding: '25px' }}
        >
          {children}
        </BoxPanel>
      )}
    </TabPanelContainer>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number,
  index: PropTypes.number,
};

const TabCreateInspection = ({
  tabs,
  variant,
  typevariant,
  initialTab,
  firstLevel,
  className,
  extraForms,
  tabsLoading,
}) => {
  const { state, pathname } = useLocation();
  const { formState, reset } = useFormContext() || {};
  const tabsContainerRef = useRef(null);

  //State
  // the first level is used to differenciate
  // the upper row of tabs (L0, L1, L2...)
  // from the bottom one (Contesto, Struttura, elementi accessori...)
  const targetValue = firstLevel
    ? (state?.inspectionLevelTab ?? initialTab)
    : state?.inspectionLevelSubTab || 0;
  const [value, setValue] = useState(targetValue);
  const [openDialog, setOpenDialog] = useState(false);
  const [tabToChange, setTabToChange] = useState();

  // Issue #1021, riapertura 2026-09-24 — "flash" della tab L0 quando
  // isL3Activity risolve in modo asincrono (CreateInspection.jsx): il
  // vecchio `useEffect(..., [state, initialTab])` sotto (ora rimosso)
  // aggiornava `value` DOPO il commit/paint del render con `initialTab`
  // ancora "vecchio" (1), quindi il browser arrivava comunque a disegnare
  // almeno un frame con la tab sbagliata selezionata prima della
  // correzione. Pattern documentato da React ("Adjusting state when a prop
  // changes", useState) per derivare `value` da `targetValue` SENZA quel
  // ritardo: lo scarto viene rilevato durante il render stesso — la
  // chiamata a `setValue` qui sotto fa ripartire immediatamente il render
  // con il valore corretto PRIMA che React lo disegni, non un intero ciclo
  // di commit+effetto dopo. Non elimina il tempo reale di attesa della
  // chiamata che risolve isL3Activity (durante quello, la tab bar mostra
  // comunque il "tentativo" L0/L1/L2, inevitabile finché il dato non
  // arriva), ma elimina il fotogramma aggiuntivo, visibile, in cui il
  // valore risulta disallineato dal prop già aggiornato.
  const [prevTargetValue, setPrevTargetValue] = useState(targetValue);
  if (targetValue !== prevTargetValue) {
    setPrevTargetValue(targetValue);
    setValue(targetValue);
  }

  useEffect(() => {
    if (firstLevel) {
      useGlobalStore.setState({ activeTabLevel: value });
    }
  }, [value]);

  const handleChange = (event, newValue) => {
    // extraForms: form RHF indipendenti montati dentro uno dei tab (oggi
    // solo L3, useInspectionL3.js — non condivide il FormProvider di questo
    // livello, quindi il suo stato non compare in formState.touchedFields
    // sopra). isDirty (non touchedFields) perché è già il segnale usato
    // dall'avviso in Accordion5CdaL3.jsx e si azzera correttamente sia al
    // caricamento (reset in handleDetail) sia dopo un salvataggio riuscito.
    const hasUnsavedExtraForm = (extraForms || []).some(
      extraForm => extraForm?.formState?.isDirty
    );
    if (
      ((Object.keys(formState?.touchedFields || {}).length > 0 &&
        !formState?.isSubmitSuccessful) ||
        hasUnsavedExtraForm) &&
      pathname.split('/').pop() !== InspectionRouteActions.VIEW
    ) {
      /* console.log('MODIFICHE NON SALVATE', formState?.touchedFields); */
      setTabToChange(newValue);
      return setOpenDialog(true);
    }
    setValue(newValue);
  };

  const handleCloseDialog = () => {
    setTabToChange();
    setOpenDialog(false);
  };

  const handleChangeTabConfirm = () => {
    reset();
    (extraForms || []).forEach(extraForm => extraForm?.reset?.());
    setValue(tabToChange);
    handleCloseDialog();
  };
  return (
    <>
      <Box sx={{ width: '100%', typography: 'body1' }}>
        <Box
          ref={tabsContainerRef}
          sx={{ borderBottom: 0, borderColor: 'divider', position: 'relative' }}
          className={`${tour_06.step_01} ${tour_06.step_03}`}
        >
          {firstLevel && (
            <MutedLevelsTourHighlight containerRef={tabsContainerRef} />
          )}
          <StyledTabs
            typevariant={typevariant}
            variant={variant}
            onChange={handleChange}
            // Issue #1021, riapertura 2026-09-24 — richiesta esplicita:
            // finché tabsLoading (isL3Activity non ancora risolto in
            // CreateInspection.jsx), nessuna tab deve risultare selezionata.
            // `value={false}` è il valore che MUI riconosce esplicitamente
            // per "nessuna tab selezionata" (non un id a caso che potrebbe
            // combaciare per sbaglio con una tab reale).
            value={tabsLoading ? false : value}
            textColor="inherit"
            indicatorColor="secondary"
            className={`${tour_03.step_01} ${className}`}
          >
            {tabs.map((el, idx) => (
              <StyledTab
                typevariant={typevariant}
                key={idx}
                label={el.label}
                icon={el.icon}
                iconPosition={el.iconPosition}
                color="info"
                value={el.value}
                disabled={el.disabled}
                muted={el.muted}
                className={el.className}
              />
            ))}
          </StyledTabs>
        </Box>
        <div className={``}>
          {/* Issue #1021, riapertura 2026-09-24 — durante tabsLoading non si
          monta NESSUN pannello (non solo la tab bar risulta neutra): senza
          questo, il pannello che corrisponde a `value` corrente (1, L0,
          finché initialTab non risolve) monta comunque il proprio
          contenuto reale, visibile anche con la tab bar sopra "spenta". */}
          {!tabsLoading &&
            tabs.map((el, idx) => (
              <TabPanel key={idx} index={idx} value={value}>
                {/* {el.content} */}
                {React.cloneElement(el.content, { setValue })}
              </TabPanel>
            ))}
        </div>
      </Box>
      <BasicDialog
        title="Attenzione"
        open={openDialog}
        onClose={handleCloseDialog}
        actions={
          <>
            <Button onClick={handleCloseDialog}>Annulla</Button>
            <Button variant="contained" onClick={handleChangeTabConfirm}>
              Scarta modifiche
            </Button>
          </>
        }
      >
        Ci sono delle modifiche non salvate nel pannello corrente. Cambiando
        pannello andranno perse, confermi di voler scartare le modifiche fatte?
      </BasicDialog>
    </>
  );
};

export { TabCreateInspection };

TabCreateInspection.defaultProps = {
  firstLevel: false,
  extraForms: [],
  tabsLoading: false,
};

TabCreateInspection.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      content: PropTypes.node,
    })
  ),
  variant: PropTypes.string,
  typevariant: PropTypes.string,
  initialTab: PropTypes.number,
  firstLevel: PropTypes.bool,
  className: PropTypes.string,
  extraForms: PropTypes.arrayOf(
    PropTypes.shape({
      formState: PropTypes.object,
      reset: PropTypes.func,
    })
  ),
  tabsLoading: PropTypes.bool,
};
